import { isRTLString } from "@qpr/client-utility-string-to-rtl";
import {
  ColorRGB,
  Element,
  ParagraphInfo,
  PortionInfo,
  TextAlign,
} from "MyDashboards/state/editorTypes";
import { CSSProperties } from "react";

function nodetoPortion(
  node: HTMLElement | null,
  currentStyles: Partial<PortionInfo>
) {
  const portion = { ...currentStyles };
  const nodeName = node?.nodeName.toLowerCase();
  const NODE_TYPE_TEXT = 3;
  if (node?.nodeType !== NODE_TYPE_TEXT) {
    if (nodeName === "strong") {
      portion.isBold = true;
    }
    if (nodeName === "em") {
      portion.isItalic = true;
    }
    if (nodeName === "ins") {
      portion.isUnderline = true;
    }
    if (node?.style.fontSize) {
      const fontSize = parseFloat(node?.style.fontSize);
      if (!Number.isNaN(fontSize)) {
        portion.fontSize = fontSize;
      }
    }
    if (node?.style.color) {
      const { color } = node?.style;
      const colorArr = color
        .trim()
        .slice(color.indexOf("(") + 1, color.indexOf(")"))
        .split(", ");
      portion.fillColor = {
        r: Number(colorArr[0]),
        g: Number(colorArr[1]),
        b: Number(colorArr[2]),
      };
    }
    if (node?.style.textAlign) {
      const { textAlign } = node?.style;
      portion.textAlign = textAlign as TextAlign;
    }
  }

  portion.text = node?.nodeValue || "";

  return portion;
}

const nodesToPortions = (node: HTMLElement, options: Partial<PortionInfo>) => {
  const portions: PortionInfo[] = [];
  const traverseNodeChildren = (
    node: HTMLElement | null,
    parentStyles?: Partial<PortionInfo>
  ) => {
    const currentStyles = {
      isBold: parentStyles?.isBold || options.isBold,
      isItalic: parentStyles?.isItalic || options.isItalic,
      isUnderline: parentStyles?.isUnderline || options.isUnderline,
      fillColor: parentStyles?.fillColor || options.fillColor,
      fontSize: parentStyles?.fontSize || options.fontSize,
      fontName: parentStyles?.fontName || options.fontName,
      textAlign: parentStyles?.textAlign || options.textAlign,
    };

    node?.childNodes.forEach((chNode) => {
      const portion = nodetoPortion(chNode as HTMLElement, currentStyles);
      portions.push(portion as PortionInfo);

      if (chNode.childNodes.length) {
        traverseNodeChildren(chNode as HTMLElement, portion);
      }
    });
  };
  traverseNodeChildren(node);
  return portions;
};

export const HTMLtoParagraphs = (
  rawHTMLValue: string,
  options: {
    fillColor: ColorRGB;
    fontSize: number;
    fontName: string;
    textAlign: TextAlign;
  }
) => {
  const parser = new window.DOMParser();
  const htmlDoc = parser.parseFromString(`${rawHTMLValue}`, "text/html");

  const paragraphList: ParagraphInfo[] = [];
  Array.from(htmlDoc.body.children).forEach((node) => {
    if (node.nodeName.toLowerCase() === "h1") {
      const par: ParagraphInfo = {
        fontSize: 60,
        portions: nodesToPortions(node as HTMLElement, options).map((p) => {
          return { ...p, fontSize: 60 };
        }),
      };
      console.log("RTL ID:", process.env.REACT_APP_RTL_LANGUAGE_ID);
      console.log("LTR ID:", process.env.REACT_APP_LTR_LANGUAGE_ID);
      par.rtl = isRTLString(node.innerHTML);
      par.languageId = isRTLString(node.innerHTML)
        ? process.env.REACT_APP_RTL_LANGUAGE_ID
        : process.env.REACT_APP_LTR_LANGUAGE_ID;
    }
    if (node.nodeName.toLowerCase() === "p") {
      let portions: PortionInfo[] = [];
      portions = nodesToPortions(node as HTMLElement, options);
      console.log(portions);
      const par: ParagraphInfo = {
        portions: portions.filter((value) => value.text),
        textAlign: (node as HTMLElement).style.textAlign,
      };
      par.rtl = isRTLString(node.innerHTML);
      console.log("RTL ID:", process.env.REACT_APP_RTL_LANGUAGE_ID);
      console.log("LTR ID:", process.env.REACT_APP_LTR_LANGUAGE_ID);
      par.languageId = isRTLString(node.innerHTML)
        ? process.env.REACT_APP_RTL_LANGUAGE_ID
        : process.env.REACT_APP_LTR_LANGUAGE_ID;
      paragraphList.push(par);
    }
    if (node.nodeName.toLowerCase() === "ol") {
      Array.from(node.children).forEach((liNode) => {
        const par: ParagraphInfo = {
          indent: 50,
          bulletNumberedStartFrom: 1,
          rtl: isRTLString(liNode.innerHTML),
          fontSize: 24,
          fontName: "Dubai",
          bulletType: "number",
          textAlign: isRTLString(liNode.innerHTML) ? "Right" : "Left",
          portions: nodesToPortions(liNode as HTMLElement, options).filter(
            (value) => value.text
          ),
          languageId: isRTLString(node.innerHTML)
            ? process.env.REACT_APP_RTL_LANGUAGE_ID
            : process.env.REACT_APP_LTR_LANGUAGE_ID,
        };
        paragraphList.push(par);
      });
    }
    if (node.nodeName.toLowerCase() === "ul") {
      Array.from(node.children).forEach((liNode) => {
        const par: ParagraphInfo = {
          indent: 50,
          bulletChar: "•",
          rtl: isRTLString(liNode.innerHTML),
          textAlign: isRTLString(liNode.innerHTML) ? "Right" : "Left",
          fontSize: 24,
          fontName: "Dubai",
          bulletType: "symbol",
          portions: nodesToPortions(liNode as HTMLElement, options).filter(
            (value) => value.text
          ),
          languageId: isRTLString(node.innerHTML)
            ? process.env.REACT_APP_RTL_LANGUAGE_ID
            : process.env.REACT_APP_LTR_LANGUAGE_ID,
        };
        paragraphList.push(par);
      });
    }
  });

  const last = paragraphList[paragraphList.length - 1];

  if (!last || !last.portions || (last && last.portions?.length > 0)) {
    paragraphList.push({
      portions: [],
      textAlign: "",
      rtl: false,
    });
  }

  return paragraphList;
};

const createHTMLStyles = (styles: CSSProperties) => {
  const res: string[] = [];
  const availableStyles: Partial<Record<keyof CSSProperties, string>> = {
    fontSize: "font-size",
    lineHeight: "line-height",
    color: "color",
    textAlign: "text-align",
    width: "width",
  };
  Object.entries(availableStyles).forEach(([key, value]) => {
    if (styles[key as keyof CSSProperties]) {
      res.push(`${value}:${styles[key as keyof CSSProperties]}`);
    }
  });
  return res.join(";");
};

const portionColorToString = (fillColor: ColorRGB) => {
  return `rgb(${fillColor.r}, ${fillColor.g}, ${fillColor.b})`;
};

const createPortionStyles = (portion: PortionInfo) => {
  const res: CSSProperties = {};
  if (portion.fontSize) {
    res.fontSize = `${portion.fontSize}px`;
    res.lineHeight = `${portion.fontSize}px`;
  }
  if (portion.fillColor) {
    res.color = portionColorToString(portion.fillColor);
  }
  if (portion.textAlign) {
    res.textAlign = portion.textAlign as TextAlign;
  }

  return res;
};

const createParagraphStyles = (paragraph: ParagraphInfo) => {
  const res: CSSProperties = {};
  if (paragraph.fontSize) {
    res.fontSize = `${paragraph.fontSize}px`;
    res.lineHeight = `${paragraph.fontSize}px`;
  }
  if (paragraph.textAlign) {
    res.textAlign = paragraph.textAlign as TextAlign;
  }

  return res;
};

export const paragraphsToHTML = (
  selectedElement?: Element,
  mapTokens?: (str: string) => string
) => {
  if (!selectedElement) return "";
  let lastIndent = 0;
  let tokens: string[] = [];
  const pushToken = (value: string, styles?: CSSProperties) => {
    let tag = value.trim();
    if (tag[0] === "<" && tag[1] !== "/" && styles) {
      const HTMLStyles = createHTMLStyles(styles);
      console.log("HTML STYLES: ", HTMLStyles);
      const tagWithoutClosingBracket = tag.slice(0, -1);
      // inject in-line styles to html tag
      tag = `${tagWithoutClosingBracket} style="${HTMLStyles}">`;
      tokens.push(tag);
      return;
    }
    tokens.push(value);
  };

  selectedElement?.paragraphs?.forEach((p) => {
    const paragraphStyles = createParagraphStyles(p);

    if (p.indent) {
      if (!lastIndent) {
        if (p.bulletType === "symbol") {
          pushToken("<ul>", paragraphStyles);
        } else {
          pushToken("<ol>", paragraphStyles);
        }
      }
      pushToken("<li>", paragraphStyles);
    } else {
      if (lastIndent > 0) {
        if (p.bulletType === "symbol") {
          pushToken("</ul>");
        } else {
          pushToken("</ol>");
        }
      }
      pushToken("<p>", { ...paragraphStyles, width: "100%" });
    }
    lastIndent = p.indent || 0;

    p.portions?.forEach((port) => {
      const styles = createPortionStyles(port);
      pushToken("<span>", styles);

      if (port.isBold) {
        pushToken("<strong>", styles);
      }
      if (port.isItalic) {
        pushToken("<em>", styles);
      }
      if (port.isUnderline) {
        pushToken("<ins>", styles);
      }
      pushToken(port.text);
      if (port.isUnderline) {
        pushToken("</ins>", styles);
      }
      if (port.isItalic) {
        pushToken("</em>", styles);
      }
      if (port.isBold) {
        pushToken("</strong>", styles);
      }
      pushToken("</span>");
    });

    if (p.indent) {
      pushToken("</li>");
    } else {
      pushToken("</p>");
    }
  });
  const HTML = tokens
    .map((tok) => {
      if (mapTokens) {
        return mapTokens(tok);
      }
      return tok;
    })
    .join("");
  console.log("Current HTML: ", HTML);
  return HTML;
};
